export interface ClientRectLike {
	left: number;
	top: number;
	right: number;
	bottom: number;
}

export interface NormalizedRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

export function normalizeRect(x1: number, y1: number, x2: number, y2: number): NormalizedRect {
	const left = Math.min(x1, x2);
	const top = Math.min(y1, y2);
	const width = Math.abs(x2 - x1);
	const height = Math.abs(y2 - y1);

	return { left, top, width, height };
}

export function rectsIntersect(a: ClientRectLike, b: ClientRectLike): boolean {
	return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

const INTERACTIVE_SELECTOR =
	'button, a, input, textarea, select, [contenteditable], search-paginator, cdk-header-cell, cdk-header-row';

export function isInteractiveTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		return false;
	}

	return !!target.closest(INTERACTIVE_SELECTOR);
}

/** Typical overlay scrollbar thickness (macOS / overlay scrollbars). */
const OVERLAY_SCROLLBAR_EDGE = 14;

/**
 * Native scrollbars are not DOM nodes, so clicks land on the scrollable host.
 * Classic scrollbars occupy layout space (`offset* - client*`); overlay ones
 * do not, so when the host overflows we treat the expected scrollbar edge as
 * non-interactive for marquee.
 */
export function isScrollbarClick(
	event: Pick<PointerEvent, 'clientX' | 'clientY'>,
	element: HTMLElement
): boolean {
	const rect = element.getBoundingClientRect();
	const style = getComputedStyle(element);
	const borderLeft = Number.parseFloat(style.borderLeftWidth) || 0;
	const borderTop = Number.parseFloat(style.borderTopWidth) || 0;
	const borderRight = Number.parseFloat(style.borderRightWidth) || 0;
	const borderBottom = Number.parseFloat(style.borderBottomWidth) || 0;

	const classicVertical = element.offsetWidth - element.clientWidth - borderLeft - borderRight;
	const classicHorizontal =
		element.offsetHeight - element.clientHeight - borderTop - borderBottom;

	const canScrollY = element.scrollHeight > element.clientHeight;
	const canScrollX = element.scrollWidth > element.clientWidth;

	const verticalEdge =
		classicVertical > 0 ? classicVertical : canScrollY ? OVERLAY_SCROLLBAR_EDGE : 0;
	const horizontalEdge =
		classicHorizontal > 0 ? classicHorizontal : canScrollX ? OVERLAY_SCROLLBAR_EDGE : 0;

	if (verticalEdge > 0) {
		if (style.direction === 'rtl') {
			if (event.clientX < rect.left + borderLeft + verticalEdge) {
				return true;
			}
		} else if (event.clientX >= rect.right - borderRight - verticalEdge) {
			return true;
		}
	}

	if (horizontalEdge > 0 && event.clientY >= rect.bottom - borderBottom - horizontalEdge) {
		return true;
	}

	return false;
}

export function isNodeTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		return false;
	}

	return !!target.closest('[data-node-id]');
}

export function isSemanticCardTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		return false;
	}

	return !!target.closest('search-card-semantic');
}

export interface AutoScrollOptions {
	edgeSize: number;
	maxSpeed: number;
}

export function getAutoScrollDeltaY(
	clientY: number,
	containerRect: ClientRectLike,
	{ edgeSize, maxSpeed }: AutoScrollOptions
): number {
	edgeSize = edgeSize > 0 ? edgeSize : 1;
	maxSpeed = Math.max(0, maxSpeed);

	const topEdge = containerRect.top + edgeSize;
	const bottomEdge = containerRect.bottom - edgeSize;

	if (clientY < topEdge) {
		const ratio = Math.min(1, (topEdge - clientY) / edgeSize);
		return -maxSpeed * ratio;
	}

	if (clientY > bottomEdge) {
		const ratio = Math.min(1, (clientY - bottomEdge) / edgeSize);
		return maxSpeed * ratio;
	}

	return 0;
}

export function normalizedRectToBounds(rect: NormalizedRect): ClientRectLike {
	return {
		left: rect.left,
		top: rect.top,
		right: rect.left + rect.width,
		bottom: rect.top + rect.height,
	};
}

export function clientPointToContentPoint(
	clientX: number,
	clientY: number,
	host: HTMLElement,
	hostRect: ClientRectLike = host.getBoundingClientRect()
): { x: number; y: number } {
	return {
		x: clientX - hostRect.left + host.scrollLeft,
		y: clientY - hostRect.top + host.scrollTop,
	};
}

export function elementContentRect(
	element: Element,
	host: HTMLElement,
	hostRect: ClientRectLike = host.getBoundingClientRect()
): ClientRectLike {
	const elementRect = element.getBoundingClientRect();

	return {
		left: elementRect.left - hostRect.left + host.scrollLeft,
		top: elementRect.top - hostRect.top + host.scrollTop,
		right: elementRect.right - hostRect.left + host.scrollLeft,
		bottom: elementRect.bottom - hostRect.top + host.scrollTop,
	};
}
